<svelte:options
  customElement={{
    tag: "oc-link-v2",
    /*                       */
    extend: window.__components.extend(),
    props: {
      asButton: { type: "Boolean", attribute: "as-button" },
      base64Href: { type: "String", attribute: "base64-href" },
      href: { type: "String" },
      iconLeft: { type: "String", attribute: "icon-left" },
      iconRight: { type: "String", attribute: "icon-right" },
      inline: { type: "String" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      rel: { type: "String" },
      size: { type: "String", reflect: true },
      target: { type: "String" },
      variant: { type: "String" },
    },
  }}
/>

<script lang="ts">
  import { InteractiveElement } from "../../../common/components/InteractiveElement";
  import type { Props } from "./LinkV2.types.js";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    variant = "primary",
    /*                                                                                  */
    size = "100",
    href = undefined,
    iconLeft = undefined,
    iconRight = undefined,
    inline = "false",
    base64Href = undefined,
    asButton = false,
    target = undefined,
    ocAriaLabel = undefined,
    rel = undefined,
  }: Props = $props();

  const Host = $host<HTMLElement>();

  const computedIconSize = $derived.by(() => {
    if (size === "inherit" || size === null) return undefined;
    if (size === "100" || size === "125") {
      return "100";
    } else {
      return "50";
    }
  });

  let underlined = $derived(() => {
    const isInsideParagraph = Host.closest("p");
    const previousSibling = Host.previousSibling;
    const nextSibling = Host.nextSibling;

    const isPreviousTextNode =
      previousSibling &&
      previousSibling.nodeType === Node.TEXT_NODE &&
      (previousSibling.nodeValue ?? "").trim().length > 0;

    const isNextTextNode =
      nextSibling &&
      nextSibling.nodeType === Node.TEXT_NODE &&
      (nextSibling.nodeValue ?? "").trim().length > 0;

    const isSurroundedByText = isPreviousTextNode || isNextTextNode;

    /*                                                                                                      */
    if ((isInsideParagraph || isSurroundedByText) && !Host.getAttribute("inline")) {
      return true;
    }
    /*                                              */
    return inline === "true";
  });
</script>

<InteractiveElement
  {Host}
  asInlineButton={asButton}
  bind:href
  {base64Href}
  {target}
  {rel}
  class={`link link--${variant} ${variant === "on-color" ? "link--over-color" : ""} ${variant === "on-color-bold" ? "link--over-color-bold" : ""}`}
  {ocAriaLabel}
  data-oc-floating-focus-v1-target
>
  {#snippet children(onslotchange)}
    <span class="link__text" class:link__text--has-icons={iconLeft || iconRight}>
      {#if iconLeft}
        <oc-icon-v1 class="link__icon-left" type={iconLeft} size={computedIconSize}></oc-icon-v1>
      {/if}
      <span
        class="link__text-inner"
        class:link__text-inner--underlined={underlined()}
        class:link__text-inner--over-color={underlined()}><slot {onslotchange} /></span
      >{#if iconRight}<oc-icon-v1 class="link__icon-right" type={iconRight} size={computedIconSize}
        ></oc-icon-v1>{/if}
    </span>
  {/snippet}
</InteractiveElement>

<DefaultStyles />

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "../../floating-focus/v1/FloatingFocusV1.shared" as floating-focus;

  /*                                  */
  /*                                                                                */
  @mixin expand-click-area() {
    &::before {
      content: "";
      position: absolute;
      inset: 0;
    }
  }

  /*                                                                                              */
  :host {
    font: tokens.$oc-component-link-100-font;
    position: relative;
    @include floating-focus.full-bleed();
  }

  :host([size="inherit"]) {
    font: inherit;
  }

  :host([size="50"]) {
    font: tokens.$oc-component-link-50-font;
  }

  :host([size="75"]) {
    font: tokens.$oc-component-link-75-font;
  }

  :host([size="125"]) {
    font: tokens.$oc-component-link-125-font;
  }

  ::slotted(a) {
    @include expand-click-area();
    text-decoration: inherit;
    color: inherit;
    outline: none;
    outline-offset: 0;
  }

  .link {
    all: unset; /*                                               */

    /*                    */
    display: inline;
    cursor: pointer;

    background: none;
    border: none;
    padding: 0;

    color: tokens.$oc-component-link-primary-text-color;
    font: inherit;

    outline: none;
    outline-offset: 0;

    &__icon-left,
    &__icon-right {
      align-self: center;
      flex: 0 0 auto;
    }

    &__text {
      &--has-icons {
        display: inline-flex;
        align-items: baseline;
        gap: tokens.$oc-component-link-gap-x;

        > slot {
          /*                                                            */
          display: inline;
        }
      }

      & > &-inner {
        text-decoration: inherit;

        &--over-color,
        &--underlined {
          text-decoration: tokens.$oc-component-link-over-color-text-decoration;
        }
      }
    }

    &--primary {
      color: tokens.$oc-component-link-primary-text-color;
    }

    &--secondary {
      color: tokens.$oc-component-link-secondary-text-color;
    }

    &--over-color,
    &--over-color-bold,
    &--underlined,
    &--underlined-bold {
      color: tokens.$oc-semantic-color-text-default;
      .link__text-inner {
        text-decoration: tokens.$oc-component-link-over-color-text-decoration;
      }
    }

    &--over-color-bold,
    &--underlined-bold {
      font-weight: tokens.$oc-component-link-over-color-bold-font-weight;
    }

    &--inverted,
    &--inverted-bold {
      color: tokens.$oc-semantic-color-text-inverted;
      .link__text-inner {
        text-decoration: tokens.$oc-component-link-inverted-text-decoration;
      }
    }

    &--inverted-bold {
      font-weight: tokens.$oc-component-link-inverted-bold-font-weight;
    }

    &.-interactive {
      @include expand-click-area();
    }
  }

  @media (hover: hover) {
    :host(:hover:not(:disabled)) .link {
      &--primary {
        color: tokens.$oc-component-link-primary-text-color-hover;
        .link__text-inner {
          text-decoration: tokens.$oc-component-link-primary-text-decoration-hover;
        }
      }

      &--secondary {
        color: tokens.$oc-component-link-secondary-text-color-hover;
        .link__text-inner {
          text-decoration: tokens.$oc-component-link-secondary-text-decoration-hover;
        }
      }

      &--over-color,
      &--over-color-bold,
      &--underlined,
      &--underlined-bold {
        opacity: tokens.$oc-component-link-over-color-opacity-hover;
      }

      &--inverted,
      &--inverted-bold {
        opacity: tokens.$oc-component-link-inverted-opacity-hover;
      }
    }
  }

  :host(:active:not(:disabled)) .link {
    &--primary {
      color: tokens.$oc-component-link-primary-text-color-active;
      .link__text-inner {
        text-decoration: tokens.$oc-component-link-primary-text-decoration-hover;
      }
    }

    &--secondary {
      color: tokens.$oc-component-link-secondary-text-color-active;
      .link__text-inner {
        text-decoration: tokens.$oc-component-link-secondary-text-decoration-hover;
      }
    }

    &--over-color,
    &--over-color-bold,
    &--underlined,
    &--underlined-bold {
      opacity: tokens.$oc-component-link-over-color-opacity-active;
    }

    &--inverted,
    &--inverted-bold {
      opacity: tokens.$oc-component-link-inverted-opacity-active;
    }
  }
</style>
